import React from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate, Link } from 'react-router-dom'
import { InferType } from 'yup'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, ButtonVariant } from '../../../../components/common/Button'
import { TextInput } from '../../../../components/common/TextInput'
import { signInSchema } from '../../../../forms/schemas/sign_in'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useFormErrorMessage } from '../../../../hooks/useFormErrorMessage'
import { theme } from '../../../../theme/theme'
import { ToastType } from '../../../../components/common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { FormControl } from '../../../../components/common/FormControl'
import { reverseUrl } from '../../../../navigation/reverseUrl'
import { login } from '../../../../api/authentication'
import {
  setSecureBearerCookies,
  setSecureUserIdCookie,
} from '../../../../helpers/authentication'
import { useAuth } from '../../../../hooks/useAuth'

export const LoginForm = (): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setUserId } = useAuth()
  const navigate = useNavigate()
  const { getErrorMessage } = useFormErrorMessage()
  const { setToastNotification } = useToastNotifications()

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(signInSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const client = useQueryClient()
  const { mutate: mutateLogin, isPending: isLoggingIn } = useMutation({
    mutationFn: async (variables: { email: string; password: string }) => {
      const resp = await login({
        email: variables.email,
        password: variables.password,
      })
      if (!resp.data.data.token || !resp.data.data.user._id) throw new Error()

      return { token: resp.data.data.token, userId: resp.data.data.user._id }
    },
    onSuccess: (data) => {
      setSecureBearerCookies(data.token)
      setSecureUserIdCookie(data.userId)
      setUserId(data.userId)
      navigate(reverseUrl('feed'))
      client.resetQueries()
    },
    onError: () => {
      // The API answers the same way whether the password was wrong or the
      // address was never proven -- deliberately, so that signing in cannot be
      // used to tell which addresses have an account. That leaves the person
      // who never finished signing up looking at what reads as a typo, with
      // nothing to tell them the way in is the link below. The hint is shown
      // for every failure, so it discloses nothing either.
      setToastNotification(
        ToastType.DANGER,
        translate('authenticationSignInView.flashMessage.signInFailure'),
        translate('authenticationSignInView.flashMessage.signInFailureHint')
      )
    },
  })

  const onSubmit = async (
    data: InferType<typeof signInSchema>
  ): Promise<void> => {
    const { email, password } = data
    mutateLogin({ password, email: email.toLowerCase() })
  }

  const onError = (): void => {
    if (!isValid) return
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  const navigateToSignUp = (): void => {
    navigate(reverseUrl('signup'))
  }

  const loading = isLoggingIn

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="flex flex-col gap-4"
    >
      <Controller
        control={control}
        name="email"
        render={({ field: { value, name, onChange, onBlur } }) => (
          <FormControl
            error={errors.email && getErrorMessage(errors.email)}
            name={name}
            label={translate('authenticationSignInView.form.labels.email')}
          >
            <TextInput
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              type="email"
              error={!!errors.email}
            />
          </FormControl>
        )}
      />
      <Controller
        control={control}
        name="password"
        render={({ field: { value, name, onChange, onBlur } }) => (
          <FormControl
            error={errors.password && getErrorMessage(errors.password)}
            name={name}
            label={translate('authenticationSignInView.form.labels.password')}
          >
            <TextInput
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              type="password"
              error={!!errors.password}
            />
          </FormControl>
        )}
      />
      <div className="mt-4 flex flex-col gap-4 md:mt-12">
        <Button
          type="submit"
          style={{
            ...theme.textVariants.paragraph,
          }}
          className="flex h-14 w-full items-center justify-center"
          label={`${translate('authenticationSignInView.signInButtonTitle')}`}
          loading={loading}
          variant={ButtonVariant.PRIMARY}
        />
        <Button
          type="button"
          style={{
            ...theme.textVariants.paragraph,
          }}
          className="flex h-14 w-full items-center justify-center"
          label={`${translate(
            'authenticationSignInView.createAccountButtonTitle'
          )}`}
          onClick={navigateToSignUp}
        />
      </div>
      {/* One way out, and it covers both reasons someone cannot get in. The
          endpoint behind it picks the mail from isVerified: an address that
          never proved itself is finishing a signup and gets the set-password
          mail, one that did is recovering an account and gets the reset mail.
          A second link would have offered a choice with no consequence -- and
          signing in answers the same way whether the password was wrong or the
          address was never proven, so there is nothing here to choose between
          anyway. */}
      <div className="mt-2 mb-4 flex justify-center md:mb-0">
        <Link
          className="underline hover:text-blue"
          to={reverseUrl('reset-password')}
        >
          {translate('authenticationSignInView.forgotPasswordButtonTitle')}
        </Link>
      </div>
    </form>
  )
}
